import React from 'react';
import { useMode } from '../../context/ModeContext';
import { useAuth } from '../../context/AuthContext';
import IndividualSidebar from './IndividualSidebar';
import OrganizationSidebar from './OrganizationSidebar';
import AdminSidebar from './AdminSidebar';

/**
 * DynamicSidebar - Renders appropriate sidebar based on current mode
 * 
 * Features:
 * - Individual Mode: Shows personal workspace sidebar (for all users)
 * - Organization Mode: Shows role-based organization sidebar (only for org members)
 * - Admin Mode: Shows admin panel sidebar (only for admin users)
 * - Proper access control - individual users never see org/admin options
 * - Smooth transitions between modes
 */
const DynamicSidebar = ({ open, collapsed, toggleSidebar, toggleCollapse }) => {
  const { 
    currentMode, 
    isModeSwitching, 
    hasOrganizationAccess, 
    hasAdminAccess 
  } = useMode();

  // Show loading state during mode switching
  if (isModeSwitching) {
    return (
      <div className={`${
        collapsed ? 'w-16' : 'w-64'
      } bg-white dark:bg-gray-800 shadow-lg border-r border-gray-200 dark:border-gray-700 transition-all duration-300 flex flex-col`}>
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  // Render admin sidebar for admin mode (only if user has admin access)
  if (currentMode === 'admin' && hasAdminAccess) {
    return (
      <AdminSidebar
        open={open}
        collapsed={collapsed}
        toggleSidebar={toggleSidebar}
        toggleCollapse={toggleCollapse}
      />
    );
  }

  // Render organization sidebar for organization mode (only if user has org access)
  if (currentMode === 'organization' && hasOrganizationAccess) {
    return (
      <OrganizationSidebar
        open={open}
        collapsed={collapsed}
        toggleSidebar={toggleSidebar}
        toggleCollapse={toggleCollapse}
      />
    );
  }

  // Render individual sidebar for individual mode (default - available to all users)
  return (
    <IndividualSidebar
      open={open}
      collapsed={collapsed}
      toggleSidebar={toggleSidebar}
      toggleCollapse={toggleCollapse}
    />
  );
};

export default DynamicSidebar;